import PropTypes from "prop-types";
import React, { Component, useState, useEffect, useRef } from "react";
import { List, Map } from "immutable";
import { withRouter } from "react-router";
import ContentStore from "../stores/ContentStore";
import MainPage from "../components/MainPage.react";
import ContentActions from "../actions/ContentActions";
import ImmutablePropTypes from "react-immutable-proptypes";
import { Form, FormGroup, ControlLabel, FormControl } from "react-bootstrap";
import renderContent from "../components/renderContent.react";
import { logPageViewRaw } from "../analytics";

const wrapHandler = (handler) => (e) => handler(e.target.value);

const ContentPicker = ({
  selectedPage,
  contentTree,
  onPageChange,
  onProjectChange,
}) => {
  function onProjectChangeLocal(e) {
    const projectId = e.target.value;
    const projectEntry = contentTree.find((entry) => entry.id == projectId);
    if (onProjectChange) {
      onProjectChange(projectId);
    }

    const firstPage = projectEntry && projectEntry.pages.first();
    if (firstPage) {
      onPageChange(firstPage.id);
    }
  }

  const projectOptions = contentTree.map((projectEntry) => (
    <option value={projectEntry.id} key={projectEntry.id}>
      {projectEntry.name}
    </option>
  ));

  // Find the project associated with this page
  const project = contentTree.find(
    (projectEntry) =>
      projectEntry.pages.findIndex((p) => p.id == selectedPage) > -1
  );

  let pageOptions = null;
  let selectedProject = null;
  if (project !== undefined) {
    pageOptions = project.pages.map((page) => (
      <option value={page.id} key={page.id}>
        {page.name}
      </option>
    ));
    selectedProject = project.id;
  }

  return (
    <div className="contentPicker">
      <Form>
        <FormGroup>
          <ControlLabel>Project:</ControlLabel>
          <FormControl
            componentClass="select"
            value={selectedProject || ""}
            placeholder="select"
            onChange={onProjectChangeLocal}
          >
            {projectOptions}
          </FormControl>
        </FormGroup>
        <FormGroup>
          <ControlLabel>Page:</ControlLabel>
          <FormControl
            componentClass="select"
            value={selectedPage || ""}
            placeholder="select"
            onChange={wrapHandler(onPageChange)}
          >
            {pageOptions}
          </FormControl>
        </FormGroup>
      </Form>
    </div>
  );
};

const Content = ({
  content,
  contentTree,
  selectedPage,
  onPageChange,
  onProjectChange,
}) => {
  const firstPage = content.getIn(["entries", selectedPage], null);

  return (
    <MainPage className="contentPage">
      {contentTree && (
        <ContentPicker
          contentTree={contentTree}
          onPageChange={onPageChange}
          onProjectChange={onProjectChange}
          selectedPage={selectedPage}
        />
      )}
      {firstPage ? renderContent(firstPage.get("fields").get("content").toJS(), content)
        : (
          <p>Loading...</p>
        )}
    </MainPage>
  );
};

Content.propTypes = {
  content: ImmutablePropTypes.map,
  contentTree: ImmutablePropTypes.list,
  selectedPage: PropTypes.string,
};

const ContentContainer = ({ params, router, location }) => {
  const [{ content, contentTree, defaultPageId }, setContent] = useState({
    content: Map(),
    contentTree: null,
    defaultPageId: null,
  });

  useEffect(() => {
    const subscription = ContentStore.addListener(() => {
      setContent({
        content: ContentStore.getState(),
        contentTree: ContentStore.getTree(),
        defaultPageId: ContentStore.getDefaultPageId(),
      });
    });
    ContentActions.loadIndex();
    return () => {
      subscription.remove();
    };
  }, []);

  // Whenever the page changes, fetch it. The previous state subscription will handle pulling.
  useEffect(() => {
    if (!params.pageId && defaultPageId) {
      router.push(`/content/${defaultPageId}`);
    } else if (params.pageId && contentTree) {
      const project = ContentStore.findProjectForPageInIndex(params.pageId);
      if (project) {
        ContentActions.loadProjectPages(project.get("id"));
      } else {
        console.log(
          `Can't find project for page ${params.pageId}. Is it real?`
        );
      }
    }
    return () => { };
  }, [
    params.pageId,
    defaultPageId,
    ContentStore.findProjectForPageInIndex(params.pageId),
  ]);

  const page = params.pageId
    ? content.getIn(["entries", params.pageId], null)
    : null;

  useEffect(() => {
    if (page) {
      const project = ContentStore.findProjectForPageInIndex(params.pageId);
      logPageViewRaw({
        location,
        page_title: `Content / ${project.get("name")} / ${page.getIn([
          "fields",
          "title",
        ])}`,
      });
    }
  }, [page]);

  const onPageChange = (val) => {
    router.push(`/content/${val}`);
  };

  return (
    <Content
      onPageChange={onPageChange}
      content={content}
      contentTree={contentTree}
      selectedPage={params.pageId}
    />
  );
};

ContentContainer.propTypes = {
  params: PropTypes.object,
  router: PropTypes.object,
  routes: PropTypes.array,
  location: PropTypes.shape({
    query: PropTypes.shape({
      run_name: PropTypes.string,
    }),
  }),
};

export default withRouter(ContentContainer);
